import fetch from 'fetch';

// The API mounts loopback-component-jsonapi, so failures come back as
// { errors: [{ status, source, title, code, detail }] }.
export interface ApiError {
  status: number;
  source: object;
  title: string;
  code: string | number;
  detail: string;
}

export interface ApiErrorBody {
  errors?: ApiError[];
}

// makeRequest used to throw away the response body, but that body is the only
// place the API explains itself - an invalid two-factor code and a bad password
// both arrive as a 401. The message stays as the status text so that callers
// matching on it keep working.
export class RequestError extends Error {
  status: number;

  // Left unset when the body is not JSON:API - callers duck-type on `errors`,
  // and an empty array would send them down the JSON:API branch with nothing
  // to report.
  errors?: ApiError[];

  constructor(status: number, statusText: string, body?: ApiErrorBody) {
    super(statusText);

    this.name = 'RequestError';
    this.status = status;

    if (body?.errors) {
      this.errors = body.errors;
    }
  }
}

export async function makeRequest(url: string, data: unknown, accessToken?: string) {
  const options = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Data-Type': 'json',
      ...(accessToken ? { Authorization: accessToken } : {}),
    },
    body: JSON.stringify(data),
  };
  const response = await fetch(url, options);

  if (!response.ok) {
    let body: ApiErrorBody | undefined;

    try {
      body = await response.json();
    } catch {
      // Not JSON - the status text is all we have to go on.
    }

    throw new RequestError(response.status, response.statusText, body);
  }

  return response.json();
}
